import { Box, Card, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  ColDef,
  ColumnMovedEvent,
  ColumnVisibleEvent,
  GridReadyEvent,
  IServerSideGetRowsParams,
  SideBarDef
} from "ag-grid-community";
import { useTranslation } from "react-i18next";
import StatusReportAction, { IReasonCodeCounter } from "../../service/statusreport.actions";
import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import { setSnackbar } from "../../../../util/state/globalAction";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import StatusReportFilterAndSearch from "./statusReportFilterAndSearch";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileSaver from "file-saver";
import EmptyState from "../../../../util/component/EmptyState";
import dayjs from "dayjs";
import { ContextOperation } from "../../context/OperationContext";
import SectionHeader from "../../../../util/component/SectionHeader";
import CustomLoader from "../../../../util/component/CustomLoader";
import AgGridOperationTable from "../common/AgGridOperationTable";
import {EXPIRED_DEFAULT_COL_DEF} from "../../config";

const StatusReportTable = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [gridApi, setGridApi] = useState<any>(null);
  const [length, setLength] = useState<number>(0);
  const [statusNumber, setStatusNumber] = useState<IReasonCodeCounter[]>([]);
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [filterData, setFilterData] = useState<any>({
    referenceLocationId: { id: "", descr: "" },
    referenceLocationType: "",
    checkinEstimatedLocationId: { id: "", descr: "" },
    plate: "",
    vin: "",
    status: { id: "", descr: "" },
    carClass: { id: "", descr: "" },
    carSegment: { id: "", descr: "" },
    carAcriss: { id: "", code: "" },
    checkinEstimatedLocationType: { id: "", descr: "" },
    statusReasons: { id: "", descr: "" },
    tractionType: { id: "", descr: "" },
    keyless: ''
  });
  const { tenant } = useContext(ContextOperation);
  const { t } = useTranslation(["operation"]);
  const statusReportAction = StatusReportAction();
  const { dispatchGlobalData, globalData } = useContext(GlobalContext);

  const endpointParams: any = {
    tenant: tenant,
    referenceLocationId: filterData.referenceLocationId.id,
    referenceLocationType: filterData.referenceLocationType,
    checkinEstimatedLocationId: !!filterData.checkinEstimatedLocationId.id
      ? filterData.checkinEstimatedLocationId.id
      : null,
    plate: !!filterData.plate ? filterData.plate.trim() : null,
    vin: !!filterData.vin ? filterData.vin.trim() : null,
    status: !!filterData.status.id ? filterData.status.id : null,
    carClass: !!filterData.carClass && filterData.carClass.descr.length > 0  ? filterData.carClass.descr : null,
    carSegment: !!filterData.carSegment && filterData.carSegment.descr.length > 0 ? filterData.carSegment.descr : null,
    carAcriss: !!filterData.carAcriss && filterData.carAcriss.code.length > 0 ? filterData.carAcriss.code : null,
    checkinEstimatedLocationType: !!filterData.checkinEstimatedLocationType.id
      ? filterData.checkinEstimatedLocationType.id
      : null,
    statusReasons: !!filterData.statusReasons.id
      ? filterData.statusReasons.id
      : null,
    tractionType: !!filterData.tractionType.id
      ? filterData.tractionType.id
      : null,
    keyless: filterData.keyless === '' ? undefined : Boolean(filterData.keyless)
  };

  const sideBar = useMemo<SideBarDef>(() => ({
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
        toolPanelParams: {
          suppressRowGroups: true,
          suppressValues: true,
          suppressPivots: true,
          suppressPivotMode: true,
          suppressColumnExpandAll: true,
        },
      },
    ],
    defaultToolPanel: '',
    position: 'right'
  }), []);

  const columnStateStorageKey = useMemo(() => {
    return `__OPERATION_PENDING.reportStatusTableColumsConfig:${globalData.user.username}`;
  }, [globalData.user.username]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    const colState = JSON.parse(localStorage.getItem(columnStateStorageKey) || '[]');
    event.columnApi.applyColumnState({
      state: colState,
      applyOrder: true
    });

    event.api.sizeColumnsToFit();

    setGridApi(event.api);
  }, [columnStateStorageKey]);

  const onColumnVisible = useCallback((event: ColumnVisibleEvent) => {
    localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
  }, [columnStateStorageKey]);

  const onColumnMoved = useCallback((event: ColumnMovedEvent) => {
    localStorage.setItem(columnStateStorageKey, JSON.stringify(event.columnApi.getColumnState()));
  }, [columnStateStorageKey]);

  const defaultColDef = useMemo<ColDef>(() => EXPIRED_DEFAULT_COL_DEF, []);

  const columnDefs: ColDef[] = useMemo(() => [
    {
      field: "vin",
      headerName: t("statusReport.table.vin"),
      minWidth: 180,
      sortable: true,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data.vin}>
          <span>{params.data.vin}</span>
        </Tooltip>
      ),
    },
    {
      field: "plate",
      headerName: t("statusReport.table.plate"),
      minWidth: 100,
      sortable: true
    },
    {
      field: "vehicleStatus",
      headerName: t("statusReport.table.status"),
      minWidth: 150,
      cellRenderer: (params: any) => (
        <span>
          {params.data.vehicleStatus === "AVAILABLE"
            ? t("internOptionSelectName.available")
            : t("internOptionSelectName.notAvailable")}
        </span>
      ),
    },
    {
      field: "reasonCode",
      headerName: t("statusReport.table.reasonCode"),
      minWidth: 150,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data?.reasonCode}>
          <span>{params.data?.reasonCode}</span>
        </Tooltip>
      ),
    },
    {
      field: "reasonCodeDescription",
      headerName: t("statusReport.table.reasonCodeDescription"),
      minWidth: 250,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data?.reasonCodeDescription}>
          <span>{params.data?.reasonCodeDescription}</span>
        </Tooltip>
      ),
    },
    {
      field: "checkOut",
      headerName: t("statusReport.table.checkoutDate"),
      minWidth: 180,
      sortable: true,
      cellRenderer: (params: any) => checkOutDateColumn(params),
    },
    {
      field: "destination",
      headerName: t("statusReport.table.destination"),
      minWidth: 150,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data?.destination}>
          <span>{params.data?.destination}</span>
        </Tooltip>
      ),
    },
    {
      field: "modelDesc",
      headerName: t("statusReport.table.model"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "brandDes",
      headerName: t("statusReport.table.brand"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "carSegmentDesc",
      headerName: t("statusReport.table.carSegment"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "carClassDesc",
      headerName: t("statusReport.table.carClass"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "acrissCode",
      headerName: t("statusReport.table.acrissCode"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "customAcrissCode",
      headerName: t("statusReport.table.sipp"),
      sortable: true,
      minWidth: 150,
    },
    {
      field: "enginePowerDesc",
      headerName: t("statusReport.table.enginePower"),
      minWidth: 150,
    },
    {
      field: "tractionTypeDescr",
      headerName: t("statusReport.table.tractionType"),
      minWidth: 180,
      cellRenderer: (params: any) => (
        <Tooltip title={params.data.tractionTypeDescr}>
          <span>{params.data.tractionTypeDescr}</span>
        </Tooltip>
      ),
    },
    {
      field: "keyless",
      headerName: t("statusReport.table.keyless"),
      minWidth: 250,
      hide: true,
      cellRenderer: (params: any) => {
        return (<span>{t(params.data?.keyless ? 'statusReport.table.yes' : 'statusReport.table.no')}</span>)
      },
    },
  ],[]);

  const checkOutDateColumn = (params: any) => {
    if (params?.data?.checkOut) {
      return (
        <span>
          {dayjs(`${params.data.checkOut}`).format("DD/MM/YYYY HH:mm")}
        </span>
      );
    }
  };

  const getAllData = () => {
    setIsLoading(true);
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const startRow = params.request.startRow ? params.request.startRow : 0;
        const endRow = params.request.endRow;

        const sortCode = params.request.sortModel[0]?.colId;
        const sortDirection  = params.request.sortModel[0]?.sort;
    
        let pageSize = 10;
        if (
          endRow !== null &&
          endRow !== undefined &&
          startRow !== null &&
          startRow !== undefined
        ) {
          pageSize = endRow - startRow;
        }
        const pageNumber = startRow / pageSize;
        setPageNumber(pageNumber);
        statusReportAction
          .getStatusReportData({ pageNumber, pageSize, sortCode, sortDirection, ...endpointParams })
          .then((response: any) => {
            params.successCallback(
              managerData(response),
              response.data.totalElements
            );
            setLength(response.data.totalElements);
            setIsLoading(false);
          })
          .catch((error) => {
            if (error.response) {
              dispatchGlobalData({
                type: setSnackbar,
                payload: {
                  snackbar: {
                    message: error.response.data.msg,
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                  },
                },
              });
              setIsLoading(false);
            } else if (error.request) {
              dispatchGlobalData({
                type: setSnackbar,
                payload: {
                  snackbar: {
                    message: t("networkErrorMessage"),
                    type: snackbarTypes.error,
                    show: true,
                    autoClose: true,
                  },
                },
              });
              setIsLoading(false);
            } else {
              setIsLoading(false);
            }
          });
        setIsLoading(false);
      },
    };
  };

  const managerData = (response: any) => {
    let rows: any = [];
    response.data.content.map((element: any) => {
      let row = {
        vin: !!element.vin ? element.vin : "",
        plate: !!element.plate ? element.plate : "",
        vehicleStatus: !!element.vehicleStatus ? element.vehicleStatus : "",
        reasonCodeDescription: !!element.reasonDescr ? element.reasonDescr : "",
        reasonCode: !!element.reasonCode ? element.reasonCode : "",
        checkOut: !!element.checkOut ? element.checkOut : "",
        destination: !!element.destination ? element.destination : "",
        modelDesc: !!element.modelDesc ? element.modelDesc : "",
        brandDes: !!element.brandDes ? element.brandDes : "",
        carSegmentDesc: !!element.carSegmentDesc ? element.carSegmentDesc : "",
        carClassDesc: !!element.carClassDesc ? element.carClassDesc : "",
        customAcrissCode: !!element.customAcrissCode
          ? element.customAcrissCode
          : "",
        acrissCode: !!element.acrissCode ? element.acrissCode : "",
        enginePowerDesc: !!element.enginePowerDesc
          ? element.enginePowerDesc
          : "",
        tractionTypeDescr: !!element.tractionTypeDescr
          ? element.tractionTypeDescr
          : "",
        keyless: element?.keyless === null ? '' : Number(element?.keyless)
      };
      rows.push(row);
    });
    return rows;
  };

  const resetData = () => {
    setFilterData({
      referenceLocationId: {
        id: "",
        descr: "",
      },
      referenceLocationType: "",
      checkinEstimatedLocationId: { id: "", descr: "" },
      plate: "",
      vin: "",
      status: { id: "", descr: "" },
      carClass: { id: "", descr: "" },
      carSegment: { id: "", descr: "" },
      carAcriss: { id: "", code: "" },
      checkinEstimatedLocationType: { id: "", descr: "" },
      statusReasons: { id: "", descr: "" },
      tractionType: { id: "", descr: "" },
      keyless: ''
    });
    setRefreshTable(!refreshTable);
  };

  const exportStatusReport = () => {
    setIsLoading(true);
    statusReportAction
      .exportStatusReportData(endpointParams)
      .then((response: any) => {
        setIsLoading(false);
        const filename =
          response?.headers["content-disposition"].split("filename=")[1];
        var blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        FileSaver.saveAs(blob, filename);
      });
  };

  useEffect(() => {
    if (gridApi && filterData?.referenceLocationId?.id) {
      gridApi.setServerSideDatasource(getAllData());
    }
  }, [gridApi, refreshTable, filterData.referenceLocationId]);

  useEffect(() => {
    if(filterData.referenceLocationId.id && filterData.referenceLocationType) {
      statusReportAction.getReasonCodeCounters(endpointParams)
      .then((response) => setStatusNumber(response));
    }
  }, [filterData.referenceLocationId,filterData.referenceLocationType, refreshTable]);

  return (
    <Box>
      <SectionHeader
        title={
          t("statusReport.titleFirst") + " " + t("statusReport.titleSecond")
        }
      />
      <Box className="outerBoxForFiltersButtonsTable">
        <StatusReportFilterAndSearch
          filterData={filterData}
          setFilterData={setFilterData}
          resetData={resetData}
          refreshTable={refreshTable}
          setRefreshTable={setRefreshTable}
          tenant={tenant}
        />

        <Box className="carGenericOuterTableBox">
          {!!filterData.referenceLocationId.id ? (
            <>
              <Card>
                <Stack direction="row" alignItems="center" px={1} minHeight={46}>
                  <Stack width="100%" direction="row" columnGap={2}>
                    {statusNumber.map((counter) => {
                      return (
                        <Box display="flex" alignItems="flex-end" columnGap={1}>
                          <Typography variant="body" lineHeight={1.6}>
                            {`${counter.code}:`}
                          </Typography>

                          <Typography variant="h6">
                            {counter.total}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Stack>
                  <Box>
                    <IconButton
                      color="accent"
                      disabled={!!length ? false : true}
                      onClick={exportStatusReport}
                    >
                      <FileDownloadIcon />
                    </IconButton>
                  </Box>
                </Stack>
              </Card>

              <AgGridOperationTable
                defaultColDef={defaultColDef}
                columnDefs={columnDefs}
                onColumnVisible={onColumnVisible}
                onColumnMoved={onColumnMoved}
                onGridReady={onGridReady}
                cacheBlockSize={pageSize}
                serverSideInfiniteScroll={true}
                rowModelType={"serverSide"}
                rowStyle={{ width: "200%" }}
                totalElements={length}
                emptyState={{
                  title: t("emptyStateGrid.title"),
                  content: t("emptyStateGrid.content")
                }}
                sideBar={sideBar}
              />

            </>
          ) : (
            <Box>
              <Stack
                sx={{ height: "400px" }}
                direction="column"
                alignItems="center"
                justifyContent="center"
                spacing={2}
              >
                <Box>
                  <EmptyState
                    title={t(
                      "statusReport.filterAndSearch.noLocationSelectedTitle"
                    )}
                    content={t(
                      "statusReport.filterAndSearch.noLocationSelectedContent"
                    )}
                  />
                </Box>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>

      {isLoading && <CustomLoader loading={isLoading} />}
    </Box>
  );
};

export default StatusReportTable;
